/**
 * Build version guard.
 *
 * Every production build embeds a unique BUILD_ID via Vite's `define`
 * (see vite.config.ts) and emits /build-info.json with the same value.
 * At runtime we compare the bundle's compiled-in BUILD_ID against the
 * server's published BUILD_ID. On mismatch we forcibly clear caches /
 * service workers and reload so every user converges on the latest deploy.
 *
 * This is independent from the service-worker update cycle and is what
 * actually guarantees "publish → everyone sees it on next refresh".
 */

declare const __BUILD_ID__: string | undefined;

export const CURRENT_BUILD_ID: string =
  typeof __BUILD_ID__ !== "undefined" && __BUILD_ID__ ? __BUILD_ID__ : "dev";

const RELOAD_GUARD_KEY = "build-version-reload-ts";
const RELOAD_COOLDOWN_MS = 30_000; // never auto-reload more than once / 30s
const RELOAD_PARAM = "__build";

type RemoteBuildInfo = { buildId?: string };

async function fetchRemoteBuildId(): Promise<string | null> {
  try {
    const url = `/build-info.json?ts=${Date.now()}`;
    const res = await fetch(url, {
      cache: "no-store",
      credentials: "omit",
      headers: { "cache-control": "no-cache" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as RemoteBuildInfo;
    return typeof data?.buildId === "string" ? data.buildId : null;
  } catch {
    return null;
  }
}

function canAutoReload(): boolean {
  try {
    const last = parseInt(sessionStorage.getItem(RELOAD_GUARD_KEY) || "0", 10);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    return true;
  } catch {
    return true;
  }
}

async function nukeCachesAndWorkers(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    }
  } catch {
    /* noop */
  }
  try {
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
    }
  } catch {
    /* noop */
  }
}

function hardReloadWithBust(remoteBuildId: string): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(RELOAD_PARAM, remoteBuildId);
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

/**
 * Compare the running bundle's BUILD_ID with the server's. If they differ,
 * wipe caches/SW and hard-reload with a cache buster.
 *
 * Returns true if a reload was triggered (caller should stop further work).
 */
export async function checkAndForceUpdate(opts?: {
  /** Skip the cooldown / reload-loop guard. Use only for explicit user actions. */
  force?: boolean;
}): Promise<boolean> {
  // Only meaningful in production builds
  if (CURRENT_BUILD_ID === "dev") return false;

  const remoteId = await fetchRemoteBuildId();
  if (!remoteId) return false;
  if (remoteId === CURRENT_BUILD_ID) return false;

  if (!opts?.force && !canAutoReload()) {
    console.warn(
      `[BuildVersion] Mismatch detected (local=${CURRENT_BUILD_ID}, remote=${remoteId}) but cooldown active.`
    );
    return false;
  }

  console.warn(
    `[BuildVersion] Stale bundle detected. local=${CURRENT_BUILD_ID} remote=${remoteId} → forcing update.`
  );

  await nukeCachesAndWorkers();
  hardReloadWithBust(remoteId);
  return true;
}

/**
 * Explicit user-triggered recovery: wipes service workers + caches and
 * hard-reloads, regardless of the reported build id. Used when the running
 * bundle is clearly out of sync with the backend (e.g. permission errors on
 * queries the current code no longer makes).
 */
export async function forceAppUpdateNow(): Promise<void> {
  await nukeCachesAndWorkers();
  hardReloadWithBust(String(Date.now()));
}
